import React from "react";

import ChangeLocation from "@/components/ChangeLocation";
import Confirmation from "@/components/Confirmation";
import ViewMedicalRecord from "@/components/Pharmacy/ViewMedicalRecord";
import Notification from "@/views/Common/Notification";
import UniversalSearch from "@/views/Common/UniversalSearch";
import SearchChat from "@/views/Doctor/MobileChat/SearchChat";
import AvailabilityListing from "@/views/Doctor/AvailabilitySettings/AvailabilityListing";
import CreateEditAvailability from "@/views/Doctor/AvailabilitySettings/CreateEditAvailability";

import {
  IDoctorConsultationSelection,
  IDoctorConsultationType,
} from "@/models/doctor";
import {
  IAvailabilitySettingPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  config,
  setIsShowChangeLocationModal,
  setIsShowNotification,
} from "@/store/slices/config";
import {
  availabilitySettings,
  fetchAvailabilitySettingsListing,
  setIsDeleteAvailability,
  setIsEditAvailability,
  setSelectedAvailabilitySetting,
  setSelectedAvailabilitySettingDetail,
} from "@/store/slices/doctor/availabilitySettings";
import { schedule, setIsCancelSchedule } from "@/store/slices/doctor/schedule";
import { chat } from "@/store/slices/chat";
import { topNavigation } from "@/store/slices/doctor/topNavigation";
import { pharmacyClinicOrDiagnostic } from "@/store/slices/pharmacyClinicDiagnostic";

import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { deleteAvailabilitySettingApi } from "@/utils/apis/doctor/availabilitySetting";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";
import { INotifyEnum, notify } from "@/utils/toaster";
import useCancelScheduleAppointments from "@/utils/hooks/useCancelScheduleAppointments";

const Overlays: React.FC = () => {
  const {
    isShowNotification,
    isShowUniversalSearch,
    isShowChangeLocationModal,
  } = useAppSelector(config) || {};
  const { isDeleteAvailability, selectedAvailabilitySettingDetail } =
    useAppSelector(availabilitySettings);
  const { isChatSearchOn } = useAppSelector(chat);
  const { isCancelSchedule } = useAppSelector(schedule);
  const { isViewPrescriptionOpen } = useAppSelector(pharmacyClinicOrDiagnostic);
  const {
    topBarItems: { isAvailabilityOpen, isCreateEditAvailabilityOpen },
  } = useAppSelector(topNavigation);

  const dispatch = useAppDispatch();

  const permissionRestrictionHandler = usePermissionRestriction();
  const { cancelScheduleAppointments } = useCancelScheduleAppointments();

  const confirmDeleteOpenCloseHandler = () =>
    dispatch(setIsDeleteAvailability(!isDeleteAvailability));

  const deleteAvailabilitySetting = async () => {
    try {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.AVAILABILITY_SETTING,
        IAvailabilitySettingPermissionEnums.DELETE_AVAILABILITY
      );
      if (!isPermissionGranted) return;
      const deleteAvailabilityResp = await deleteAvailabilitySettingApi(
        selectedAvailabilitySettingDetail?.id!
      );
      if (deleteAvailabilityResp.statusCode === IAPIStatusCode.SUCCESS) {
        dispatch(fetchAvailabilitySettingsListing({}));
        dispatch(setSelectedAvailabilitySetting(null));
        dispatch(setSelectedAvailabilitySettingDetail(null));
        dispatch(setIsEditAvailability(false));
        notify(INotifyEnum.SUCCESS, deleteAvailabilityResp.message);
      } else {
        notify(INotifyEnum.ERROR, deleteAvailabilityResp.message);
      }
      confirmDeleteOpenCloseHandler();
    } catch (error) {
      console.log("error", error);
    }
  };

  const openCloseCancelAppointmentConfirmation = () =>
    dispatch(setIsCancelSchedule(!isCancelSchedule));

  return (
    <>
      {isDeleteAvailability && (
        <Confirmation
          isOpen={isDeleteAvailability}
          closeHandler={confirmDeleteOpenCloseHandler}
          title="delete_availability"
          alterMessage={"confirm_delete"}
          name={
            selectedAvailabilitySettingDetail?.type_of_consultation ===
            IDoctorConsultationType.IN_CLINIC
              ? selectedAvailabilitySettingDetail?.clinic_name ?? ""
              : IDoctorConsultationSelection.VIDEO_CONSULTATION
          }
          actionHandler={deleteAvailabilitySetting}
        />
      )}
      {isViewPrescriptionOpen && <ViewMedicalRecord />}
      {isShowUniversalSearch && <UniversalSearch />}
      {isChatSearchOn && <SearchChat />}
      {isShowNotification && (
        <Notification
          isOpen={isShowNotification}
          closeHandler={() => dispatch(setIsShowNotification(false))}
        />
      )}
      {isShowChangeLocationModal && (
        <ChangeLocation
          isOpen={isShowChangeLocationModal}
          onClose={() => dispatch(setIsShowChangeLocationModal(false))}
        />
      )}
      {isCancelSchedule && (
        <Confirmation
          isOpen={isCancelSchedule}
          closeHandler={openCloseCancelAppointmentConfirmation}
          title="cancel_appointments"
          alterMessage={"confirm_cancel_appoinment"}
          actionHandler={() => {
            cancelScheduleAppointments();
            openCloseCancelAppointmentConfirmation();
          }}
        />
      )}
      {isAvailabilityOpen && <AvailabilityListing />}
      {isCreateEditAvailabilityOpen && <CreateEditAvailability />}
    </>
  );
};

export default Overlays;
